// React imports
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

// External library imports
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import axios from "axios";

// Local imports
import { RootState } from "../../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../../components/Methods/SnackBar";
import { viewActiveUsers, assignKRAUrl } from "../../../Service/Apis";
import { Employee } from "./Types/AssignKRATypes";

const AssignKRAForm: React.FC = () => {
  // 1. useState declarations
  const [assignmentType, setAssignmentType] = useState<"all" | "specific">("all");
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  // 2. const variables (hooks, Redux, computed constants)
  const { templateId } = useParams<{ templateId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();
  const designationName = location.state?.designationName || "Unknown";
  const formData = location.state?.formData;

  // 3. Function declarations
  const fetchEmployees = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await dispatch(fetchData(viewActiveUsers));
      if (response && Array.isArray(response)) {
        // Filter employees by designation
        const filtered = response.filter(
          (emp: Employee) => emp.designation === designationName
        );
        setEmployees(filtered);
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to load employees";
      showToast(errorMessage, "error", true);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (): void => {
    let filtered = [...employees];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (emp) =>
          emp.first_name?.toLowerCase().includes(query) ||
          emp.last_name?.toLowerCase().includes(query) ||
          emp.employee_code?.toLowerCase().includes(query) ||
          emp.email?.toLowerCase().includes(query)
      );
    }

    setFilteredEmployees(filtered);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      const allIds = new Set(filteredEmployees.map((emp) => emp.id));
      setSelectedEmployees(allIds);
    } else {
      setSelectedEmployees(new Set());
    }
  };

  const handleSelectEmployee = (employeeId: string): void => {
    const newSelected = new Set(selectedEmployees);
    if (newSelected.has(employeeId)) {
      newSelected.delete(employeeId);
    } else {
      newSelected.add(employeeId);
    }
    setSelectedEmployees(newSelected);
  };

  const handleAssign = async (): Promise<void> => {
    let employeeIds: string[] = [];

    if (assignmentType === "all") {
      employeeIds = employees.map((emp) => emp.id);
    } else {
      employeeIds = Array.from(selectedEmployees);
    }

    if (employeeIds.length === 0) {
      showToast("Please select at least one employee", "error", true);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        template_id: templateId,
        employee_ids: employeeIds,
      };

      
      const token = localStorage.getItem("token");
      const response = await axios.post(assignKRAUrl, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.success) {
        const { created_count, skipped_count, total_requested } = response.data;
        
        let message = `KRA assigned successfully!\n`;
        message += `✓ Assigned: ${created_count} employee(s)\n`;
        if (skipped_count > 0) {
          message += `⚠ Skipped: ${skipped_count} (already assigned)\n`;
        }
        
        showToast(message, "success", true);
        
        // Navigate back to designations after short delay
        setTimeout(() => {
          navigate("/pms/designations");
        }, 2000);
      } else {
        throw new Error(response.data?.message || "Failed to assign KRA");
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to assign KRA. Please try again.";
      showToast(errorMessage, "error", true);
    } finally {
      setSubmitting(false);
    }
  };

  // 4. useEffect hooks (just above return)
  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [employees, searchQuery]);

  // 5. Return JSX
  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 10, mb: 5 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/pms/designations")}
          sx={{ mb: 2 }}
        >
          Back to Designations
        </Button>
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}
        >
          Assign KRA
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Template: <strong>{formData?.form_name || "KRA Template"}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Designation: <strong>{designationName}</strong>
        </Typography>
      </Box>

      {/* Assignment Options */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
          👥 Select Employees
        </Typography>

        <FormControl component="fieldset">
          <RadioGroup
            value={assignmentType}
            onChange={(e) => setAssignmentType(e.target.value as "all" | "specific")}
          >
            <FormControlLabel
              value="all"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1">
                    Assign to All {designationName}s
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {employees.length} employee(s) will receive this KRA
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="specific"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1">Assign to Specific Employees</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Select individual employees from the list below
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {assignmentType === "specific" && (
          <>
            <Divider sx={{ my: 3 }} />

            {/* Filters */}
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search by name, code, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>

            {/* Employee Table */}
            {filteredEmployees.length === 0 ? (
              <Alert severity="info">
                No employees found matching your filters.
              </Alert>
            ) : (
              <>
                <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          filteredEmployees.length > 0 &&
                          selectedEmployees.size === filteredEmployees.length
                        }
                        indeterminate={
                          selectedEmployees.size > 0 &&
                          selectedEmployees.size < filteredEmployees.length
                        }
                        onChange={handleSelectAll}
                      />
                    }
                    label={`Select All (${filteredEmployees.length})`}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {selectedEmployees.size} selected
                  </Typography>
                </Box>

                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox"></TableCell>
                        <TableCell>Employee Code</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredEmployees.map((employee) => (
                        <TableRow
                          key={employee.id}
                          hover
                          onClick={() => handleSelectEmployee(employee.id)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedEmployees.has(employee.id)}
                              onChange={() => handleSelectEmployee(employee.id)}
                            />
                          </TableCell>
                          <TableCell>{employee.employee_code}</TableCell>
                          <TableCell>
                            {employee.first_name} {employee.last_name}
                          </TableCell>
                          <TableCell>{employee.email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate("/pms/designations")}
          size="large"
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
          onClick={handleAssign}
          size="large"
          disabled={submitting || (assignmentType === "specific" && selectedEmployees.size === 0)}
        >
          {submitting ? "Assigning..." : "Assign to Employees"}
        </Button>
      </Box>
    </Container>
  );
};

export default AssignKRAForm;
